import { collection, doc, getDoc, getDocs, query, limit, getCountFromServer, collectionGroup, updateDoc, deleteDoc } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from './firebase';

export interface AdminUser {
  id: string;
  email: string;
  name?: string;
  displayName?: string;
  role?: string;
  systemStatus?: string;
  createdAt?: string;
  [key: string]: any;
}

export interface AdminWorkspace {
  id: string;
  name: string;
  firmName?: string;
  ownerId: string;
  ownerName?: string;
  memberCount?: number;
  createdAt?: string;
  workspaceType?: 'solo' | 'firm';
  [key: string]: any;
}

export interface SystemStats {
  totalUsers: number;
  activeWorkspaces: number;
  totalCases: number;
}

export interface UserWorkspaceProfile {
  workspaceId: string;
  workspaceName: string;
  role: string;
  status: string;
  joinedAt?: string;
}

export interface WorkspaceMemberProfile {
  id: string; // userId
  name: string;
  email: string;
  role: string;
  status: string;
  joinedAt?: string;
  photoURL?: string;
  systemStatus?: string;
}

export const adminService = {
  async getSystemStats(): Promise<SystemStats> {
    try {
      const usersSnap = await getCountFromServer(collection(db, 'users'));
      const workspacesSnap = await getCountFromServer(collection(db, 'workspaces'));
      
      let totalCases = 0;
      try {
        const casesSnap = await getCountFromServer(collectionGroup(db, 'cases'));
        totalCases = casesSnap.data().count;
      } catch (e) {
        // Fallback: collectionGroup might fail if not permitted by rules or missing index
        console.warn("Could not aggregate cases via collectionGroup.", e);
      }

      return {
        totalUsers: usersSnap.data().count,
        activeWorkspaces: workspacesSnap.data().count,
        totalCases: totalCases,
      };
    } catch (error) {
      handleFirestoreError(error, OperationType.GET, 'system-stats(users/workspaces)');
      return { totalUsers: 0, activeWorkspaces: 0, totalCases: 0 };
    }
  },

  async getAllUsers(): Promise<AdminUser[]> {
    try {
      const q = query(collection(db, 'users'), limit(100)); // Limit for safety in MVP
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AdminUser[];
    } catch (error) {
      handleFirestoreError(error, OperationType.LIST, 'users');
      return [];
    }
  },

  async getAllWorkspaces(): Promise<AdminWorkspace[]> {
    try {
      const q = query(collection(db, 'workspaces'), limit(100));
      const querySnapshot = await getDocs(q);
      
      const workspaces = await Promise.all(querySnapshot.docs.map(async (wsDoc) => {
        const data = wsDoc.data();
        let ownerName = data.ownerId;
        
        try {
          if (data.ownerId) {
            const uSnap = await getDoc(doc(db, 'users', data.ownerId));
            if (uSnap.exists()) {
              ownerName = uSnap.data().displayName || uSnap.data().name || data.ownerId;
            }
          }
        } catch (e) {}

        return {
          id: wsDoc.id,
          ownerName,
          ...data
        } as AdminWorkspace;
      }));
      
      return workspaces;
    } catch (error) {
      handleFirestoreError(error, OperationType.LIST, 'workspaces');
      return [];
    }
  },

  async getAdminUserProfile(userId: string): Promise<{ user: AdminUser; workspaces: UserWorkspaceProfile[] }> {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) {
        throw new Error('User not found');
      }
      
      const user = { id: userSnap.id, ...userSnap.data() } as AdminUser;
      let workspaces: UserWorkspaceProfile[] = [];

      // Try fetching memberships using getDocs on all workspaces
      // In a very large app, you'd use collectionGroup with indexes or Cloud Functions.
      const allWsSnap = await getDocs(query(collection(db, 'workspaces'), limit(100)));
      
      await Promise.all(allWsSnap.docs.map(async (wsDoc) => {
        const memberSnap = await getDoc(doc(db, 'workspaces', wsDoc.id, 'members', userId));
        if (memberSnap.exists()) {
          const memberData = memberSnap.data();
          workspaces.push({
            workspaceId: wsDoc.id,
            workspaceName: wsDoc.data().firmName || wsDoc.data().name || 'Unnamed Workspace',
            role: memberData.role || 'user',
            status: memberData.status || 'unknown',
            joinedAt: memberData.joinedAt
          });
        }
      }));

      return { user, workspaces };
    } catch (error) {
      handleFirestoreError(error, OperationType.GET, `users/${userId}`);
      throw error;
    }
  },

  async getAdminWorkspaceProfile(workspaceId: string): Promise<{ workspace: AdminWorkspace; members: WorkspaceMemberProfile[] }> {
    try {
      const wsRef = doc(db, 'workspaces', workspaceId);
      const wsSnap = await getDoc(wsRef);
      if (!wsSnap.exists()) {
        throw new Error('Workspace not found');
      }

      const workspace = { id: wsSnap.id, ...wsSnap.data() } as AdminWorkspace;
      const members: WorkspaceMemberProfile[] = [];

      const membersSnap = await getDocs(collection(db, 'workspaces', workspaceId, 'members'));
      
      await Promise.all(membersSnap.docs.map(async (mDoc) => {
        const mData = mDoc.data();
        let name = 'Unknown';
        let email = 'Unknown';
        
        try {
          const uSnap = await getDoc(doc(db, 'users', mDoc.id));
          if (uSnap.exists()) {
            const uData = uSnap.data();
            name = uData.displayName || uData.name || 'Unknown';
            email = uData.email || 'Unknown';
          }
        } catch (e) {}

        members.push({
          id: mDoc.id,
          name,
          email,
          role: mData.role || 'user',
          status: mData.status || 'unknown',
          joinedAt: mData.joinedAt
        });
      }));

      return { workspace, members };
    } catch (error) {
      handleFirestoreError(error, OperationType.GET, `workspaces/${workspaceId}`);
      throw error;
    }
  },

  async toggleUserStatus(userId: string, currentStatus: string = 'active'): Promise<void> {
    try {
      const newStatus = currentStatus === 'suspended' ? 'active' : 'suspended';
      await updateDoc(doc(db, 'users', userId), { systemStatus: newStatus });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `users/${userId}`);
      throw error;
    }
  },

  async removeMemberFromWorkspace(workspaceId: string, memberId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, 'workspaces', workspaceId, 'members', memberId));
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, `workspaces/${workspaceId}/members/${memberId}`);
      throw error;
    }
  }
};
